import React, { useState, useEffect } from 'react';
import api from '../api/axios.js';
import placeholder from '../assets/images/default-profile-avatar.svg';
import drBudi from '../assets/images/dr-budi.svg';
import drDesfa from '../assets/images/dr-desfa.svg';

function resolveImageUrl(path) {
  if (!path) return placeholder;
  try {
    const p = String(path);
    let url;
    if (p.startsWith('http') || p.startsWith('/')) {
      url = p;
    } else {
      const base = (api.defaults?.baseURL || '').replace(/\/api\/?$/i, '').replace(/\/$/, '');
      url = base ? `${base}/${String(path).replace(/^\//, '')}` : String(path);
    }
    // Tambahkan cache busting seperti di dashboard dokter
    return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  } catch {
    return path;
  }
}

const HARI_URUTAN = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];
const HARI_KE_DAY = { Minggu: 0, Senin: 1, Selasa: 2, Rabu: 3, Kamis: 4, Jumat: 5, Sabtu: 6 };

function sortJadwal(jadwals) {
  return [...(jadwals || [])].sort(
    (a, b) => HARI_URUTAN.indexOf(a.hari) - HARI_URUTAN.indexOf(b.hari)
  );
}

function tanggalJadwalBerikutnya(hari) {
  const target = HARI_KE_DAY[hari];
  if (target === undefined) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (target - today.getDay() + 7) % 7;
  const d = new Date(today);
  d.setDate(today.getDate() + diff);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatJam(jam) {
  return jam ? String(jam).substring(0, 5) : '—';
}

function getJadwalList(doctor) {
  return sortJadwal(doctor.jadwal_dokter || doctor.jadwalDokter);
}

export function ClinicDoctors() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/public/dokter')
      .then((response) => {
        // ✅ response berbentuk { data: [...] }
        const data = response.data?.data || response.data || [];
        // tambahkan demo local image untuk dokter yang belum punya foto
        const mapped = (data || []).map((d) => {
          // Cek semua kemungkinan field foto termasuk photo_url dari backend
          const hasPhoto = d.foto || d.photo || d.avatar || d.photo_url;
          if (hasPhoto) return d;
          // Jika tidak punya foto, gunakan demo local image
          const name = String(d.nama || '').toLowerCase();
          if (name.includes('budi')) return { ...d, foto: drBudi };
          if (name.includes('desfa') || name.includes('des')) return { ...d, foto: drDesfa };
          return d;
        });
        setDoctors(mapped);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Gagal memuat data dokter:", err);
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="text-center py-20">Memuat data dokter...</div>;

  return (
    <section id="jadwal" style={{ padding: '3rem 1rem' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16 clinic-section-header">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 clinic-section-title">
            Tim Dokter Profesional
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto clinic-section-desc">
            Dokter gigi berpengalaman & ramah — pilih jadwal yang cocok dan daftar langsung dari aplikasi
          </p>
          {error && (
            <p className="text-orange-600 mt-2 bg-orange-50 p-3 rounded-lg clinic-section-error">
              Gagal memuat data dokter dari server.
            </p>
          )}
        </div>

        {doctors.length === 0 ? (
          <p className="text-center text-gray-500">Belum ada data dokter tersedia.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 dokter-grid">
            {doctors.map((doctor) => {
              const jadwals = getJadwalList(doctor);
              return (
                <div key={doctor.id} className="bg-white rounded-2xl shadow-lg dokter-card" style={{ padding: '1.25rem' }}>
                  <div className="dokter-card__avatar" style={{
                    width: '6rem', height: '6rem',
                    background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', borderRadius: '50%',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'white',
                    fontSize: '1.35rem', marginBottom: '0.65rem', overflow: 'hidden',
                    marginLeft: 'auto', marginRight: 'auto',
                    boxShadow: '0 8px 24px rgba(14, 165, 233, 0.35)',
                    border: '3px solid #fff',
                    outline: '2px solid #bae6fd'
                  }}>
                    <img
                      src={resolveImageUrl(
                        doctor.foto || doctor.photo || doctor.photo_url || doctor.avatar ||
                        doctor.user?.photo_url || doctor.user?.foto || doctor.user?.photo || doctor.user?.avatar ||
                        doctor.profile?.photo_url || doctor.profile?.foto || doctor.profile?.photo ||
                        doctor.role?.photo_url || doctor.role?.foto || doctor.role?.photo || doctor.role?.avatar
                      )}
                      alt={doctor.nama}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = placeholder; }}
                    />
                  </div>

                  <h3 style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.2rem', fontSize: '1.05rem', textAlign: 'center' }}>
                    {doctor.nama}
                  </h3>

                  <p style={{ color: '#0ea5e9', fontWeight: 600, marginBottom: '0.65rem', fontSize: '0.85rem', textAlign: 'center' }}>
                    {doctor.spesialisasi}
                  </p>

                  {doctor.sedang_istirahat && (
                    <p style={{ color: '#c2410c', fontSize: '0.75rem', margin: '0 0 0.5rem', fontWeight: 600 }}>
                      ☕ Sedang istirahat
                    </p>
                  )}

                  {jadwals.length > 0 && !doctor.sedang_istirahat ? (
                    <div className="dokter-card__jadwal">
                      <p className="dokter-card__jadwal-label">📅 Jadwal Praktik</p>
                      <ul className="dokter-card__jadwal-list">
                        {jadwals.map((jadwal) => (
                          <li key={jadwal.id} className="dokter-card__jadwal-item">
                            <span className="dokter-card__jadwal-hari">{jadwal.hari}</span>
                            <span className="dokter-card__jadwal-tanggal">{tanggalJadwalBerikutnya(jadwal.hari)}</span>
                            <span className="dokter-card__jadwal-jam">
                              {!jadwal.status ? (
                                <span style={{ color: '#f97316', fontWeight: 600 }}>🚫 Libur</span>
                              ) : (
                                '🕐 16:00 - 21:00'
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: 0 }}>
                      🕐 16:00 - 21:00
                    </p>
                  )}

                  {doctor.hari_libur && (
                    <p style={{ color: '#f97316', fontSize: '0.75rem', marginTop: '0.2rem', marginBottom: 0 }}>
                      🚫 Libur: {doctor.hari_libur}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default ClinicDoctors;